import { mergeComponentCollections } from '@ea-lab/reactive-json'
import { BuilderHtmlField } from './BuilderHtmlField'
// Import DIRECT, fichier par fichier, et non par `./primitives` (index) : un
// module d'index entraîne TOUS ses modules à effet de bord (`ChartJS.register`…)
// dans chaque paquet qui monte un bloc, runtime d'îlots des pages publiques
// compris. Les primitifs lourds sont différés (`lazyPlugin`).
import { ResolvedBackground } from './primitives/ResolvedBackground'
import { LazyImage } from './primitives/LazyImage'
import { ResolvedLink } from './primitives/ResolvedLink'
import { LucideIcon } from './primitives/LucideIcon'
import { ClassMap } from './primitives/ClassMap'
import { MapText } from './primitives/MapText'
import { TermLabel } from './primitives/TermLabel'
import { FormatDate } from './primitives/FormatDate'
import { FormatCurrency } from './primitives/FormatCurrency'
import { Formula } from './primitives/Formula'
import { PublishToggle } from './primitives/PublishToggle'
import { InlineEditCell } from './primitives/InlineEditCell'
import { DecoupledDeleteButton } from './primitives/DecoupledDeleteButton'
import { RolesEditor } from './primitives/RolesEditor'
import { YoutubeEmbed } from './primitives/YoutubeEmbed'
import { CountUp } from './primitives/CountUp'
import { Countdown } from './primitives/Countdown'
import { NavDropdown } from './primitives/NavDropdown'
import { MegaMenu } from './primitives/MegaMenu'
import { NavShell } from './primitives/NavShell'
import { RawSvg } from './primitives/RawSvg'
import { BuilderRowField } from './primitives/BuilderRowField'
import { BuilderRowAction } from './primitives/BuilderRowAction'
import { BuilderRowToolbar } from './primitives/BuilderRowToolbar'
import { PathLink } from './primitives/PathLink'
import { FloatingDropdown } from './primitives/FloatingDropdown'
import { FacetList } from './primitives/FacetList'
import { RangeFacet } from './primitives/RangeFacet'
import { ActiveFilterChips } from './primitives/ActiveFilterChips'
import { Pagination } from './primitives/Pagination'
import { SearchField } from './primitives/SearchField'
import { LoadingOverlay } from './primitives/LoadingOverlay'
import { FilteredGallery } from './primitives/FilteredGallery'
import { DateRangeFacet } from './primitives/DateRangeFacet'
import { DateRangeAutoInterval } from './primitives/DateRangeAutoInterval'
import { ScoredQuiz } from './primitives/ScoredQuiz'
import { RadarChart } from './primitives/RadarChart'
import { FilterCount } from './primitives/FilterCount'
import { UrlSync } from './primitives/UrlSync'
import { BrowserStorageSync } from './primitives/BrowserStorageSync'
import { JsonTreeView } from './primitives/JsonTreeView'
import { ScrollShadow } from './primitives/ScrollShadow'
import { ParentFrameHeight } from './primitives/ParentFrameHeight'
import { GroupByMonth } from './primitives/GroupByMonth'
import { BuilderUnit } from './primitives/BuilderUnit'
import { UnitMount, FragmentEmbed } from './primitives/UnitMount'
import { lazyPlugin } from './lazyPlugin'
// Champs d'affichage colorés (read-only) de la liste ES /tasks : les blocs du
// builder les utilisent aussi, et la collection du builder n'hérite pas de celle d'App.jsx.
// Depuis leur fichier, pas depuis `../task` : l'index du domaine entraîne le
// Kanban, la feuille de temps et l'éditeur de tâche.
import { TaskColorBadge, TaskPriorityIcon, TaskAssigneeAvatar, TaskTagsInline } from '../task/TaskListFields'
// Saisie d'un ENTIER borné (nombre de mois d'un widget de tableau de bord) : la
// collection du builder n'hérite pas de celle d'App.jsx — sans elle, le
// `type: IntegerField` d'un bloc ne rend RIEN, en silence, et le réglage est
// invisible dans le panneau de filtres.
import { IntegerField } from '../shared/fields/IntegerField'
// Primitives partagées avec les pages YAML : un bloc « fiche » (record éditable)
// s'en sert pour le bouton d'enregistrement du Syncable, l'avatar d'initiales et
// la liste de liens vers des entités. La collection du builder n'hérite pas de
// celle d'App.jsx — sans elles, le bloc rend un « type inconnu ».
import { DataSyncSaveButton } from '../shared/ui/DataSyncSaveButton'
import { InitialsAvatar } from '../shared/ui/InitialsAvatar'
import { EntityLinkList } from '../shared/ui/EntityLinkList'
// Modale de détail des widgets de tableau de bord (clic sur une cellule ou un
// segment) : la collection du builder n'hérite pas de celle d'App.jsx — sans
// elle, le `type: Modal` d'un bloc ne rend RIEN, en silence. Le clic écrivait
// bien la sélection dans le store, mais la modale n'était jamais montée.
import { Modal } from '../shared/ui/Modal'
// Preuve de travail anti-robot des formulaires publics (contact, lettre
// d'information, inscription) : la collection du builder n'hérite pas de celle
// d'App.jsx — sans elle, le `type: AltchaField` d'un bloc ne rend RIEN, en
// silence, et le formulaire ne part jamais (le jeton reste vide).
import { AltchaField } from '../shared/fields/AltchaField'
// Même raison pour la pièce jointe d'un formulaire public : sans elle, le
// `type: Base64FileField` d'un bloc ne rend RIEN, en silence.
import { Base64FileField } from '../shared/fields/Base64FileField'
// Réactions personnalisées : elles ne sont PAS dans le noyau reactive-json et la
// collection du builder n'hérite pas de celle d'App.jsx. Sans elles, l'action
// `esFetch` d'un bloc (filtres à facettes, tri, pagination) est un `what:`
// inconnu — le clic ne déclenche rien, en silence. (`toggleData` et
// `collectField` sont dans le cœur depuis reactive-json 2.11.)
import { esFetchReaction } from '../shared/reactions/esFetchReaction'
import { stringifyDataReaction } from '../shared/reactions/stringifyDataReaction'
import { joinDataReaction } from '../shared/reactions/joinDataReaction'
import { downloadCsvReaction } from '../shared/reactions/downloadCsvReaction'
import { flattenEsHits } from './dataProcessors/flattenEsHits'
import { parseFragmentBuild } from './dataProcessors/parseFragmentBuild'
import { useDataSyncPolicy } from './hooks/useDataSyncPolicy'
import { submitData as submitDataWithDryRun } from './rj-overrides/submitData'

// Différés : chacun tire une grosse dépendance (chart.js, embla, react-grid-layout
// et l'enveloppe d'édition des blocs imbriqués, la chaîne unified/remark, la
// recherche floue) que la plupart des pages ne rendent pas.
const ChartJs = lazyPlugin(() => import('./primitives/ChartJs').then((m) => m.ChartJs), 'ChartJs')
const ChartLoader = lazyPlugin(() => import('./primitives/ChartLoader').then((m) => m.ChartLoader), 'ChartLoader')
const Carousel = lazyPlugin(() => import('./primitives/Carousel').then((m) => m.Carousel), 'Carousel')
const GridLayout = lazyPlugin(() => import('./GridLayout').then((m) => m.GridLayout), 'GridLayout')
const ColumnsLayout = lazyPlugin(() => import('./ColumnsLayout').then((m) => m.ColumnsLayout), 'ColumnsLayout')
const MarkdownContent = lazyPlugin(() => import('./MarkdownContent').then((m) => m.MarkdownContent), 'MarkdownContent')
const FuzzySelectField = lazyPlugin(() => import('../shared/fields/FuzzySelectField').then((m) => m.default), 'FuzzySelectField')

/**
 * The plugin collection every builder bloc's `ReactiveJsonRoot` renders with
 * (see `BuilderBlockMount`). The builder has its OWN collection (it does NOT
 * inherit the app-wide one in `App.jsx`) — so any primitive a block YAML uses
 * must be listed here. Keep this aligned with `App.jsx`'s collection for
 * components shared between the public site and the builder (e.g. ChartLoader).
 *
 * Extracted from `BuilderCanvas.jsx` so `BuilderBlockMount` can live in its own
 * module — breaking the `BuilderCanvas ↔ BuilderGridLayout` import cycle.
 */
export const builderPlugins = mergeComponentCollections([{
  element: {
    BuilderHtmlField,
    ResolvedBackground,
    LazyImage,
    ResolvedLink,
    LucideIcon,
    ClassMap,
    MapText,
    TermLabel,
    FormatDate,
    FormatCurrency,
    // Résultat d'une formule arithmétique sur des valeurs du store (calculateurs).
    Formula,
    PublishToggle,
    InlineEditCell,
    DecoupledDeleteButton,
    RolesEditor,
    YoutubeEmbed,
    CountUp,
    Countdown,
    NavDropdown,
    MegaMenu,
  NavShell,
    RawSvg,
    // Inline list editing: a cell edited in place (emitted by expandEditable
    // for `editable: {field, column}`), the standalone add button, the per-row
    // up/down/insert/remove toolbar.
    BuilderRowField,
    BuilderRowAction,
    BuilderRowToolbar,
    PathLink,
    FloatingDropdown,
    FacetList,
    RangeFacet,
    ActiveFilterChips,
    Pagination,
    SearchField,
    LoadingOverlay,
    FilteredGallery,
    DateRangeFacet,
    DateRangeAutoInterval,
    ChartJs,
    ScoredQuiz,
    RadarChart,
    ChartLoader,
    // Panneaux de filtres des widgets de tableau de bord : le compteur
    // « N filtres » et le multi-select de référentiel.
    FilterCount,
    // Filtres PARTAGÉS d'un tableau de bord : miroir store ↔ URL, seul canal de
    // diffusion entre des cellules qui sont chacune un ReactiveJsonRoot isolé.
    UrlSync,
    // Choix du visiteur retenus d'une page à l'autre sans compte (sélection à
    // comparer, listes sauvegardées) : miroir store ↔ stockage local du navigateur.
    BrowserStorageSync,
    FuzzySelectField,
    IntegerField,
    AltchaField,
    Base64FileField,
    GridLayout,
    ColumnsLayout,
    MarkdownContent,
    JsonTreeView,
    ScrollShadow,
    ParentFrameHeight,
    Carousel,
    GroupByMonth,
    DataSyncSaveButton,
    InitialsAvatar,
    EntityLinkList,
    Modal,
    TaskColorBadge,
    TaskPriorityIcon,
    TaskAssigneeAvatar,
    TaskTagsInline,
    BuilderUnit,
    FragmentEmbed,
    UnitMount,
  },
  // Cadence d'écriture des DataSync des blocs (enregistrement manuel dans le
  // builder, groupement implicite sur l'item_url) : un point d'extension du
  // DataSync du cœur, plus une copie du composant. Les sous-roots en héritent.
  hook: {
    useDataSyncPolicy,
  },
  reaction: {
    esFetch: esFetchReaction,
    // Mêmes noms que dans le cœur, mais anciens paramètres encore utilisés par du
    // contenu : chacune aiguille selon la forme de ses paramètres (cf. leur fichier).
    stringifyData: stringifyDataReaction,
    // Façade de vidéo (src d'iframe composée au clic) — et toute URL à composer dans un bloc.
    joinData: joinDataReaction,
    // Export d'une liste du store en fichier CSV (tableau comparatif du comparateur
    // de produits) : Blob + lien de téléchargement, qu'aucune action YAML n'exprime.
    downloadCsv: downloadCsvReaction,
    // Override core submitData : dans l'APERÇU du builder, un envoi vers un flux
    // ECA (`/eca/…`) ne part pas (envoi à blanc, cf. BuilderDryRunPanel). Partout
    // ailleurs — pages publiques, îlots —, c'est le submitData du moteur, tel quel.
    submitData: submitDataWithDryRun,
  },
  dataProcessor: {
    'flatten-es-hits': { callback: flattenEsHits, order: 0 },
    'parse-fragment-build': { callback: parseFragmentBuild, order: 0 },
  },
}])
